import React, { useState, useEffect } from 'react';
import { usePortalAuth } from '../../contexts/PortalAuthContext';
import { farmTeamAPI } from '../../services/portalAPI';
import PortalFooter from '../../components/Layout/PortalFooter';
import '../../styles/portal-brand.css';
import './PortalFarmTeam.css';

interface FarmTeamMember {
  id: number;
  user_id: number;
  email: string;
  first_name: string;
  last_name: string;
  full_name: string;
  farm_role: string;
  farm_role_display: string;
  permissions: {
    can_manage_farm_employees: boolean;
    can_create_tasks: boolean;
    can_complete_tasks: boolean;
    can_edit_farm_map: boolean;
    can_log_movements: boolean;
    can_create_movement_plans: boolean;
  };
  created_at: string;
  created_by: string | null;
  last_login: string | null;
  is_current_user: boolean;
}

interface FarmRole {
  value: string;
  label: string;
}

interface FarmTeamData {
  team_members: FarmTeamMember[];
  can_manage_farm_employees: boolean;
  farm_name: string;
  farm_roles: FarmRole[];
}

const FARM_ROLE_DESCRIPTIONS: Record<string, string> = {
  'farm_owner': 'Full access to all farm features including employee management',
  'foreman': 'Create and manage tasks, log movements, create movement plans',
  'crew': 'View and complete assigned tasks, log movements',
  'farm_viewer': 'View-only access to farm data',
};

const PortalFarmTeam: React.FC = () => {
  const { user } = usePortalAuth();
  const [teamData, setTeamData] = useState<FarmTeamData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Add member modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [addForm, setAddForm] = useState({ email: '', first_name: '', last_name: '', farm_role: 'crew' });
  const [addLoading, setAddLoading] = useState(false);

  // Edit member modal state
  const [editingMember, setEditingMember] = useState<FarmTeamMember | null>(null);
  const [editRole, setEditRole] = useState('');
  const [editLoading, setEditLoading] = useState(false);

  // Remove confirmation state
  const [removingMember, setRemovingMember] = useState<FarmTeamMember | null>(null);
  const [removeLoading, setRemoveLoading] = useState(false);

  useEffect(() => {
    loadTeamData();
  }, []);

  const loadTeamData = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await farmTeamAPI.listMembers();
      setTeamData(response.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load farm team data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddMember = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!addForm.email) return;

    try {
      setAddLoading(true);
      setError('');
      const response = await farmTeamAPI.addMember({
        email: addForm.email,
        first_name: addForm.first_name,
        last_name: addForm.last_name,
        farm_role: addForm.farm_role,
        send_invite: true,
      });
      setSuccess(response.data.message);
      setShowAddModal(false);
      setAddForm({ email: '', first_name: '', last_name: '', farm_role: 'crew' });
      loadTeamData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to add team member');
    } finally {
      setAddLoading(false);
    }
  };

  const handleUpdateRole = async () => {
    if (!editingMember || !editRole) return;

    try {
      setEditLoading(true);
      setError('');
      const response = await farmTeamAPI.updateMember({
        member_id: editingMember.id,
        farm_role: editRole,
      });
      setSuccess(response.data.message);
      setEditingMember(null);
      loadTeamData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update team member');
    } finally {
      setEditLoading(false);
    }
  };

  const handleRemoveMember = async () => {
    if (!removingMember) return;

    try {
      setRemoveLoading(true);
      setError('');
      const response = await farmTeamAPI.removeMember(removingMember.id);
      setSuccess(response.data.message);
      setRemovingMember(null);
      loadTeamData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to remove team member');
    } finally {
      setRemoveLoading(false);
    }
  };

  const handleResendInvite = async (member: FarmTeamMember) => {
    try {
      setError('');
      const response = await farmTeamAPI.resendInvite(member.id);
      setSuccess(response.data.message);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to resend invite');
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getRoleBadgeClass = (role: string) => {
    switch (role) {
      case 'farm_owner': return 'role-badge role-owner';
      case 'foreman': return 'role-badge role-foreman';
      case 'crew': return 'role-badge role-crew';
      case 'farm_viewer': return 'role-badge role-viewer';
      default: return 'role-badge';
    }
  };

  if (loading) {
    return (
      <div className="portal-page">
        <div className="portal-container">
          <div className="loading">Loading farm team...</div>
        </div>
        <PortalFooter />
      </div>
    );
  }

  return (
    <div className="portal-page">
      <div className="portal-container farm-team-page">
        <div className="page-header">
          <div className="page-header-content">
            <h1>Farm Team Management</h1>
            <p className="page-subtitle">Manage employees who can access your farm's portal features</p>
          </div>
          {teamData?.can_manage_farm_employees && (
            <button className="btn btn-primary" onClick={() => setShowAddModal(true)}>
              Add Team Member
            </button>
          )}
        </div>

        {error && (
          <div className="alert alert-error" onClick={() => setError('')}>
            {error}
          </div>
        )}

        {success && (
          <div className="alert alert-success" onClick={() => setSuccess('')}>
            {success}
          </div>
        )}

        {/* Team Members List */}
        <div className="team-list">
          {teamData?.team_members.length === 0 ? (
            <div className="empty-state">
              <p>No farm team members yet.</p>
              {teamData?.can_manage_farm_employees && (
                <p className="text-muted">Add your first team member to get started.</p>
              )}
            </div>
          ) : (
            teamData?.team_members.map((member) => (
              <div key={member.id} className={`team-member-card ${member.is_current_user ? 'current-user' : ''}`}>
                <div className="member-info">
                  <div className="member-avatar">
                    {member.first_name?.[0] || member.email[0].toUpperCase()}
                  </div>
                  <div className="member-details">
                    <div className="member-name">
                      {member.full_name || member.email}
                      {member.is_current_user && <span className="you-badge">You</span>}
                    </div>
                    <div className="member-email">{member.email}</div>
                    <div className="member-meta">
                      <span className={getRoleBadgeClass(member.farm_role)}>
                        {member.farm_role_display}
                      </span>
                      <span className="meta-item">Last login: {formatDate(member.last_login)}</span>
                    </div>
                  </div>
                </div>

                {teamData?.can_manage_farm_employees && !member.is_current_user && (
                  <div className="member-actions">
                    <button
                      className="btn-text"
                      onClick={() => {
                        setEditingMember(member);
                        setEditRole(member.farm_role);
                      }}
                    >
                      Edit Role
                    </button>
                    {!member.last_login && (
                      <button
                        className="btn-text"
                        onClick={() => handleResendInvite(member)}
                      >
                        Resend Invite
                      </button>
                    )}
                    <button
                      className="btn-text btn-danger"
                      onClick={() => setRemovingMember(member)}
                    >
                      Remove
                    </button>
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        {/* Permission Summary */}
        <div className="permissions-summary">
          <h3>Role Permissions</h3>
          <div className="permissions-grid">
            {teamData?.farm_roles.map((role) => (
              <div key={role.value} className="permission-card">
                <div className={getRoleBadgeClass(role.value)}>{role.label}</div>
                <p className="permission-description">
                  {FARM_ROLE_DESCRIPTIONS[role.value] || 'No description available'}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Add Member Modal */}
        {showAddModal && (
          <div className="modal-overlay" onClick={() => !addLoading && setShowAddModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Add Farm Team Member</h2>
                <button
                  className="modal-close"
                  onClick={() => !addLoading && setShowAddModal(false)}
                  disabled={addLoading}
                >
                  &times;
                </button>
              </div>
              <form onSubmit={handleAddMember}>
                <div className="modal-body">
                  <div className="form-group">
                    <label htmlFor="email">Email Address *</label>
                    <input
                      type="email"
                      id="email"
                      value={addForm.email}
                      onChange={(e) => setAddForm({ ...addForm, email: e.target.value })}
                      required
                      disabled={addLoading}
                      placeholder="employee@example.com"
                    />
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="first_name">First Name</label>
                      <input
                        type="text"
                        id="first_name"
                        value={addForm.first_name}
                        onChange={(e) => setAddForm({ ...addForm, first_name: e.target.value })}
                        disabled={addLoading}
                        placeholder="John"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="last_name">Last Name</label>
                      <input
                        type="text"
                        id="last_name"
                        value={addForm.last_name}
                        onChange={(e) => setAddForm({ ...addForm, last_name: e.target.value })}
                        disabled={addLoading}
                        placeholder="Smith"
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label htmlFor="farm_role">Role *</label>
                    <select
                      id="farm_role"
                      value={addForm.farm_role}
                      onChange={(e) => setAddForm({ ...addForm, farm_role: e.target.value })}
                      disabled={addLoading}
                    >
                      {teamData?.farm_roles.map((role) => (
                        <option key={role.value} value={role.value}>
                          {role.label}
                        </option>
                      ))}
                    </select>
                    <small className="form-text">
                      {FARM_ROLE_DESCRIPTIONS[addForm.farm_role]}
                    </small>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowAddModal(false)}
                    disabled={addLoading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={addLoading}
                  >
                    {addLoading ? 'Adding...' : 'Add Member & Send Invite'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Edit Role Modal */}
        {editingMember && (
          <div className="modal-overlay" onClick={() => !editLoading && setEditingMember(null)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Edit Role</h2>
                <button
                  className="modal-close"
                  onClick={() => !editLoading && setEditingMember(null)}
                  disabled={editLoading}
                >
                  &times;
                </button>
              </div>
              <div className="modal-body">
                <p className="edit-member-info">
                  Editing role for <strong>{editingMember.full_name || editingMember.email}</strong>
                </p>
                <div className="form-group">
                  <label htmlFor="edit_role">New Role</label>
                  <select
                    id="edit_role"
                    value={editRole}
                    onChange={(e) => setEditRole(e.target.value)}
                    disabled={editLoading}
                  >
                    {teamData?.farm_roles.map((role) => (
                      <option key={role.value} value={role.value}>
                        {role.label}
                      </option>
                    ))}
                  </select>
                  <small className="form-text">
                    {FARM_ROLE_DESCRIPTIONS[editRole]}
                  </small>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setEditingMember(null)}
                  disabled={editLoading}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleUpdateRole}
                  disabled={editLoading || editRole === editingMember.farm_role}
                >
                  {editLoading ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Remove Confirmation Modal */}
        {removingMember && (
          <div className="modal-overlay" onClick={() => !removeLoading && setRemovingMember(null)}>
            <div className="modal-content modal-small" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header modal-header-danger">
                <h2>Remove Team Member</h2>
                <button
                  className="modal-close"
                  onClick={() => !removeLoading && setRemovingMember(null)}
                  disabled={removeLoading}
                >
                  &times;
                </button>
              </div>
              <div className="modal-body">
                <p>
                  Are you sure you want to remove <strong>{removingMember.full_name || removingMember.email}</strong> from your farm team?
                </p>
                <p className="text-muted">
                  They will lose access to farm features like tasks and movement tracking.
                </p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setRemovingMember(null)}
                  disabled={removeLoading}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleRemoveMember}
                  disabled={removeLoading}
                >
                  {removeLoading ? 'Removing...' : 'Remove Member'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      <PortalFooter />
    </div>
  );
};

export default PortalFarmTeam;
